/**
 * W30(a) — WHICH DAY A TASK IS WRITTEN TO, SAID OUT LOUD.
 *
 * `docs/EVERFIT-MAP.md` §9-ter, read live at four depths on his account. Their tasks tab has **no
 * add button in its bar at all**: the only door is a `+` at the hovered cell's top-right, and it
 * is what carries the day — `?view=create-task&date=2026-09-16` — into the form, which then
 * prints it back as `SET DATE · Sep 16, 2026 (Wed)`.
 *
 * 🔴 **READING OURS AFTER THEIRS FOUND THE REAL DEFECT, AND IT IS BIGGER THAN THE COPY.**
 * `Client.tsx` passed `day={today}` to `TaskEditor` as a hard-coded constant while the calendar
 * beside it pages freely, and the form printed no date anywhere. So a coach looking at
 * `Oct 19 – Nov 1` who pressed `Add a task` wrote it into THIS week, and nothing on the screen
 * disagreed with them. **That is the defect W22(g) fixed on the Training calendar six hours
 * earlier, in the same component family** — the cell's `+` names the day, the day travels, and
 * the surface that writes states what it will write.
 *
 * ⚠️ **THE SENTENCE AND THE SAVE ASK ONE FUNCTION.** `targetDays` is what the form prints its
 * count from AND what `TaskEditor.save()` loops over. A form that says "seven days" from its own
 * arithmetic while the save writes six is this repo's defining bug class with a calendar in it,
 * and it is the cheap version of the `WeeklyPlanner`/validator disagreement the codex records.
 */

import { longDate, mondayKey, realDay } from "./weekLine";
import { shiftDayKey } from "./days";

/** The three-letter weekday, Monday-first, read off the SAME UTC instant `longDate` reads. */
const WEEKDAY = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

/* 🔴 W30(g), THE SAME DAY: `realDay` NOW LIVES IN `weekLine.ts` AND IS IMPORTED, NOT COPIED.
 *
 * It was written here first, inside W30(a), with the shared formatters FILED because widening
 * them was its own change with its own stills. That change has landed, so `longDate`, `shortDate`,
 * `axisDate` and `dayDivider` ask this same function — and keeping a second copy here would be
 * two definitions of *is this a real day*, which is this repo's defining bug class with a calendar
 * in it. The reasoning that earned the rule is in `weekLine.ts` beside the definition.
 */

/**
 * `Sep 16, 2026 (Wed)` — **their form exactly**, and built from `longDate` rather than beside it.
 *
 * ⚠️ NO SECOND DATE FORMATTER. `longDate` is already documented as *"Everfit's own form, for a
 * date in a TABLE or on a CARD"*; this adds the bracketed weekday their task form adds and
 * nothing else, so a date cannot come to be spelled two ways in one product.
 *
 * ⚠️ A KEY THAT IS NOT A REAL DAY COMES BACK UNCHANGED, WITH NO BRACKET (see `realDay`). A
 * `(Invalid Date)` suffix would be a guess wearing a fact's clothes, and the thing a coach is
 * being asked to trust here is precisely the date.
 */
export function taskDayLabel(dayKey: string): string {
  const d = realDay(dayKey);
  if (d === null) return dayKey;
  return `${longDate(dayKey)} (${WEEKDAY[(d.getUTCDay() + 6) % 7]})`;
}

/**
 * Every day the save will write, in order — ONE day, or the Monday-first week around it.
 *
 * 🔴 MONDAY-FIRST, ALWAYS, never the locale's week. `TaskEditor` already carried the phone's own
 * warning about this: two calendars disagree by a WHOLE WEEK in a US locale on a Sunday, and the
 * task lands on the wrong seven days. The rule moves here so the sentence and the loop share it.
 *
 * ⚠️ A KEY THAT IS NOT A REAL DAY RETURNS THE EMPTY LIST, never `[dayKey]`. A save that writes a row keyed
 * on a string the database will refuse is worse than a save that refuses here, and the caller can
 * see `[]` and say so.
 */
export function targetDays(dayKey: string, everyDay: boolean): string[] {
  const d = realDay(dayKey);
  if (d === null) return [];
  if (!everyDay) return [dayKey];
  const monday = mondayKey(d);
  return Array.from({ length: 7 }, (_, i) =>
    shiftDayKey(new Date(monday + "T00:00:00Z"), i));
}

/**
 * What the form prints above its own save, DERIVED from `targetDays` and never counted twice.
 *
 * ⚠️ THE ONE-DAY ARM NAMES THE DAY AND THE WEEK ARM NAMES BOTH ENDS. "Every day this week" is
 * exactly the phrase that let the old bug hide: *which* week was the question nobody could see
 * the answer to. Both arms therefore carry a real date, which is the whole point of the change.
 */
export function targetSentence(dayKey: string, everyDay: boolean): string {
  const span = endsOf(dayKey, everyDay);
  if (span === null) return "No day chosen.";
  const { first, last, count } = span;
  if (count === 1) return `This task goes on ${taskDayLabel(first)}.`;
  return `This task goes on all ${String(count)} days of `
    + `${longDate(first)} – ${longDate(last)}.`;
}

/** The save button's own words, so the button and the sentence cannot name different days. */
export function saveLabel(dayKey: string, everyDay: boolean): string {
  const span = endsOf(dayKey, everyDay);
  if (span === null) return "Add the task";
  return span.count === 1
    ? `Add to ${longDate(span.first)}`
    : `Add to ${longDate(span.first)} – ${longDate(span.last)}`;
}

/**
 * Both ends of what `targetDays` returned, or `null` when it returned nothing.
 *
 * ⚠️ THE ENDS ARE READ OFF THE LIST, never re-derived, and the COUNT comes from the list too —
 * so a sentence saying "seven days" cannot outlive a `targetDays` that one day returns six. The
 * two callers above are a print and a button label, and they are the pair most likely to drift.
 */
function endsOf(dayKey: string, everyDay: boolean):
  { first: string; last: string; count: number } | null {
  const days = targetDays(dayKey, everyDay);
  const first = days[0];
  const last = days[days.length - 1];
  if (first === undefined || last === undefined) return null;
  return { first, last, count: days.length };
}
